import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';

import {
  HowToGetAccessContent,
  PaywallRegion,
  WhyUpgradeContent,
} from '../LessonLimitPaywall/paywallContent';

export { resolveRegion } from '../LessonLimitPaywall/paywallContent';

export function useTestPrepPaywallContent(region: PaywallRegion) {
  const { t } = useTranslate();

  const whyUpgrade = useMemo((): WhyUpgradeContent => {
    switch (region) {
      case 'TX':
        return {
          statPercent: '30%',
          statHeadline: t(
            'TestPrepPaywall.WhyUpgrade.TX.StatHeadline',
            'Growth in Meets and Masters on STAAR!',
          ),
          statSubtext: t(
            'TestPrepPaywall.WhyUpgrade.TX.StatSubtext',
            'Teachers using Curipod 2-3 times per week see double-digit growth in students\u2019 STAAR scores.',
          ),
          caseStudies: [
            {
              label: t(
                'TestPrepPaywall.WhyUpgrade.TX.CaseStudy1Label',
                'Efficacy & Impact',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-green-valley',
            },
            {
              label: t(
                'TestPrepPaywall.WhyUpgrade.TX.CaseStudy2Label',
                'Real-Time Feedback, Real Results',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-south-east-texas',
            },
          ],
          features: [
            t(
              'TestPrepPaywall.WhyUpgrade.TX.Feature1',
              'Unlimited STAAR lesson creation',
            ),
            t('TestPrepPaywall.WhyUpgrade.TX.Feature2', 'Full STAAR prep library'),
            t(
              'TestPrepPaywall.WhyUpgrade.TX.Feature3',
              '3,000+ schools already on the plan',
            ),
          ],
        };

      case 'CA':
        return {
          statPercent: '40%',
          statHeadline: t(
            'TestPrepPaywall.WhyUpgrade.CA.StatHeadline',
            'Higher writing scores on CAASPP!',
          ),
          statSubtext: t(
            'TestPrepPaywall.WhyUpgrade.CA.StatSubtext',
            'Teachers using Curipod 2-3 times per week see double-digit growth in students\u2019 CAASPP scores.',
          ),
          caseStudies: [
            {
              label: t(
                'TestPrepPaywall.WhyUpgrade.CA.CaseStudy1Label',
                'Efficacy & Impact',
              ),
              url: 'https://curipod.com/case-studies/california-writing-achievement',
            },
            {
              label: t(
                'TestPrepPaywall.WhyUpgrade.CA.CaseStudy2Label',
                'Southern CA \u2013 Transformative Writing',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-southern-ca',
            },
          ],
          features: [
            t(
              'TestPrepPaywall.WhyUpgrade.CA.Feature1',
              'Unlimited CAASPP lesson creation',
            ),
            t(
              'TestPrepPaywall.WhyUpgrade.CA.Feature2',
              'Full CAASPP & ELPAC prep library',
            ),
            t(
              'TestPrepPaywall.WhyUpgrade.CA.Feature3',
              '3,000+ schools already on the plan',
            ),
          ],
        };

      default:
        return {
          statPercent: '40%',
          statHeadline: t(
            'TestPrepPaywall.WhyUpgrade.OTHER.StatHeadline',
            'Higher writing scores on state test scores!',
          ),
          statSubtext: t(
            'TestPrepPaywall.WhyUpgrade.OTHER.StatSubtext',
            'Teachers who use Curipod 2-3 times per week see double-digit growth in students\u2019 state test scores.',
          ),
          caseStudies: [
            {
              label: t(
                'TestPrepPaywall.WhyUpgrade.OTHER.CaseStudy1Label',
                'Efficacy & Impact',
              ),
              url: 'https://curipod.com/case-studies/curipod-case-study-green-valley',
            },
            {
              label: t(
                'TestPrepPaywall.WhyUpgrade.OTHER.CaseStudy2Label',
                'Writing Achievement',
              ),
              url: 'https://curipod.com/case-studies/california-writing-achievement',
            },
          ],
          features: [
            t(
              'TestPrepPaywall.WhyUpgrade.OTHER.Feature1',
              'Unlimited test prep lesson creation',
            ),
            t(
              'TestPrepPaywall.WhyUpgrade.OTHER.Feature2',
              'Full test prep library access',
            ),
            t(
              'TestPrepPaywall.WhyUpgrade.OTHER.Feature3',
              '3,000+ schools already on the plan',
            ),
          ],
        };
    }
  }, [region, t]);

  const howToGetAccess = useMemo((): HowToGetAccessContent => {
    switch (region) {
      case 'TX':
        return {
          intro: t(
            'TestPrepPaywall.HowToGetAccess.TX.Intro',
            "Curipod's paid plan is purchased by schools. We do not have a paid individual teacher plan. We know this process can take time, and we have a <strong>dedicated team here to help you</strong>.",
          ),
          notAloneTitle: t(
            'TestPrepPaywall.HowToGetAccess.TX.NotAloneTitle',
            'You are not alone!',
          ),
          notAlone: t(
            'TestPrepPaywall.HowToGetAccess.TX.NotAlone',
            'Our team has helped hundreds of teachers in TX get access to the Curipod school plan! With our many case studies documenting <strong>double-digit STAAR score growth</strong>, AI-driven writing feedback, and TELPAS support features, we can help schools leverage <strong>different funding sources</strong> to purchase Curipod.',
          ),
          steps: [
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step1Title',
                'Invite your principal to your classroom to observe Curipod in action',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step1Description',
                'Show how your students react to instant feedback on their STAAR practice.',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step2Title',
                "Show your principal your students' progress",
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step2Description',
                'How are scores improving? How many students are getting better results?',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step3Title',
                'Share our case studies with your admin',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step3Description',
                'Curipod is proven to increase student achievement by dozens of campuses across Texas.',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step4Title',
                'Book a meeting with us to help you',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.TX.Step4Description',
                'Bonus to bring your principal or instructional coach, but we can also support you alone \u{1F60A}',
              ),
            },
          ],
          weCanHelpTitle: t(
            'TestPrepPaywall.HowToGetAccess.TX.WeCanHelpTitle',
            'We can help you with:',
          ),
          weCanHelp: [
            t(
              'TestPrepPaywall.HowToGetAccess.TX.WeCanHelp1',
              'Finding funding sources similar schools use for Curipod.',
            ),
            t(
              'TestPrepPaywall.HowToGetAccess.TX.WeCanHelp2',
              'Sharing how other teachers got their school to implement Curipod for STAAR practice.',
            ),
            t(
              'TestPrepPaywall.HowToGetAccess.TX.WeCanHelp3',
              'Answering any questions you might have.',
            ),
          ],
        };

      case 'CA':
        return {
          intro: t(
            'TestPrepPaywall.HowToGetAccess.CA.Intro',
            "Curipod's paid plan is purchased by schools. We do not have a paid individual teacher plan. We know this process can take time, and we have a <strong>dedicated team here to help you</strong>.",
          ),
          notAloneTitle: t(
            'TestPrepPaywall.HowToGetAccess.CA.NotAloneTitle',
            'You are not alone!',
          ),
          notAlone: t(
            'TestPrepPaywall.HowToGetAccess.CA.NotAlone',
            'Our team has helped hundreds of teachers in CA get access to the Curipod school plan! With our many case studies documenting <strong>double-digit CAASPP score growth</strong>, AI-driven writing feedback, and English learner support features, we can help schools leverage <strong>different funding sources</strong> to purchase Curipod.',
          ),
          steps: [
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step1Title',
                'Invite your principal to your classroom to observe Curipod in action',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step1Description',
                'Show how your students react to instant feedback on their CAASPP practice.',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step2Title',
                "Show your principal your students' progress",
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step2Description',
                'How are scores improving? How many students are getting better results?',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step3Title',
                'Share our case studies with your admin',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step3Description',
                'Curipod is proven to increase student achievement by dozens of campuses across California.',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step4Title',
                'Book a meeting with us to help you',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.CA.Step4Description',
                'Bonus to bring your principal or instructional coach, but we can also support you alone \u{1F60A}',
              ),
            },
          ],
          weCanHelpTitle: t(
            'TestPrepPaywall.HowToGetAccess.CA.WeCanHelpTitle',
            'We can help you with:',
          ),
          weCanHelp: [
            t(
              'TestPrepPaywall.HowToGetAccess.CA.WeCanHelp1',
              'Finding funding sources similar schools use for Curipod.',
            ),
            t(
              'TestPrepPaywall.HowToGetAccess.CA.WeCanHelp2',
              'Sharing how other teachers got their school to implement Curipod for CAASPP practice.',
            ),
            t(
              'TestPrepPaywall.HowToGetAccess.CA.WeCanHelp3',
              'Answering any questions you might have.',
            ),
          ],
        };

      default:
        return {
          intro: t(
            'TestPrepPaywall.HowToGetAccess.OTHER.Intro',
            "Curipod's paid plan is purchased by schools. We do not have a paid individual teacher plan. We know this process can take time, and we have a <strong>dedicated team here to help you</strong>.",
          ),
          notAloneTitle: t(
            'TestPrepPaywall.HowToGetAccess.OTHER.NotAloneTitle',
            'You are not alone!',
          ),
          notAlone: t(
            'TestPrepPaywall.HowToGetAccess.OTHER.NotAlone',
            'Our team supports teachers with this every day! With our many case studies documenting <strong>double-digit growth in writing scores</strong>, AI-driven writing feedback, we can help schools leverage <strong>different funding sources</strong> to purchase Curipod.',
          ),
          steps: [
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step1Title',
                'Invite your principal to your classroom to observe Curipod in action',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step1Description',
                'Show how your students engage and learn with Curipod lessons.',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step2Title',
                "Show your principal your students' progress",
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step2Description',
                'How are scores improving? How many students are getting better results?',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step3Title',
                'Share our case studies with your admin',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step3Description',
                'Curipod is proven to increase student achievement by dozens of campuses like yours.',
              ),
            },
            {
              title: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step4Title',
                'Book a meeting with us to help you',
              ),
              description: t(
                'TestPrepPaywall.HowToGetAccess.OTHER.Step4Description',
                'Bonus to bring your principal or instructional coach, but we can also support you alone \u{1F60A}',
              ),
            },
          ],
          weCanHelpTitle: t(
            'TestPrepPaywall.HowToGetAccess.OTHER.WeCanHelpTitle',
            'We can help you with:',
          ),
          weCanHelp: [
            t(
              'TestPrepPaywall.HowToGetAccess.OTHER.WeCanHelp1',
              'Finding funding sources similar schools use for Curipod.',
            ),
            t(
              'TestPrepPaywall.HowToGetAccess.OTHER.WeCanHelp2',
              'Sharing how other teachers got their school to implement Curipod.',
            ),
            t(
              'TestPrepPaywall.HowToGetAccess.OTHER.WeCanHelp3',
              'Answering any questions you might have.',
            ),
          ],
        };
    }
  }, [region, t]);

  return { whyUpgrade, howToGetAccess };
}
